import { withErrorHandler } from 'services/Api';
import type {
    ProxyRequest,
    ProxyResponse,
    MicroserviceProxyClientError,
    ProxyResponseMicroserviceError,
} from './typesProxy';
import { toast } from 'react-toastify';
import { getTokenPrefix, TokenPrefix } from 'lib/utils/embedded-app.utils';

export class MicroserviceProxyClient {
    /**
     * Async function for retrieving the access token.
     * Must be set from outside: proxyClient.getAccessTokenSilently = ...
     */
    public getAccessTokenSilently?: () => Promise<string>;

    private readonly tokenPrefix: TokenPrefix = getTokenPrefix();

    async fetch<Res = any>(
        req: ProxyRequest & { method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' },
        businessAccountId: string,
        skipErrorToast: boolean = false,
    ): Promise<Res | void> {
        const headers = await this.getHeaders();
        try {
            const res = await fetch(`/api/business-accounts/${businessAccountId}/microservice-proxy`, {
                method: 'POST',
                headers,
                body: JSON.stringify(req),
            });

            if (res.status === 401) {
                console.error('401 - reloading');
                window.location.reload();
                throw new MicroserviceProxyClientErr({
                    status: 401,
                    code: 'Unauthorized',
                    message: res.statusText || 'Invalid or expired token',
                    details: null,
                });
            }

            const data = (await res.json()) as ProxyResponse<Res>;

            if (res.status === 200) {
                if (data.status >= 400) {
                    const microserviceApiError = data as ProxyResponseMicroserviceError;
                    let message: string = 'Microservice API rejection';
                    let code: string | number = 'Microservice API rejection';
                    let detail: any = null;

                    if (microserviceApiError.response instanceof Array) {
                        message =
                            typeof microserviceApiError.response[0].message === 'string'
                                ? microserviceApiError.response[0].message
                                : 'Microservice API rejection';
                        code =
                            typeof microserviceApiError.response[0].errorCode === 'string'
                                ? microserviceApiError.response[0].errorCode
                                : 'Microservice API rejection';
                        detail = microserviceApiError.response[0].fields ?? null;
                        if (microserviceApiError.response.length > 1) {
                            const messages = microserviceApiError.response
                                .filter(error => error.message)
                                .map(error => error.message)
                                .join(' | ');
                            const codes = microserviceApiError.response
                                .filter(error => error.errorCode)
                                .map(error => error.errorCode)
                                .join(' | ');
                            const details = microserviceApiError.response
                                .filter(error => error.fields)
                                .map(error => error.fields)
                                .join(' | ');
                            message = messages;
                            code = codes;
                            detail = details;
                        }
                    } else {
                        message =
                            microserviceApiError.response.message ??
                            microserviceApiError.response.error ??
                            'Microservice API rejection';
                        code = microserviceApiError.response.code ?? 'Microservice API rejection';
                        detail = microserviceApiError.response.details ?? null;
                    }

                    throw await withErrorHandler(async () => {
                        if (!skipErrorToast) {
                            toast.error(
                                `Error ${microserviceApiError.status}: ${message ?? code ?? 'Microservice API rejection'}`,
                            );
                        }
                        return new MicroserviceProxyClientErr({
                            status: microserviceApiError.status,
                            code,
                            message,
                            details: detail,
                        });
                    });
                }

                if (MicroserviceProxyClient.isProxySuccessResponse(data)) {
                    return data.response;
                }
            }

            throw await withErrorHandler(async () => {
                const message =
                    typeof data === 'object'
                        ? (data as any).detail || (data as any).message || res.statusText || 'Proxy rejection'
                        : 'Proxy rejection';

                if (!skipErrorToast) {
                    toast.error(`Error ${res.status}: ${message}`);
                }
                return new MicroserviceProxyClientErr({
                    status: res.status,
                    code: null,
                    message: message,
                    details: data,
                });
            });
        } catch (err) {
            const microserviceApiError = err as MicroserviceProxyClientError;
            if (!skipErrorToast) {
                if (microserviceApiError?.status) {
                    toast.error(
                        `Error ${microserviceApiError.status}: ${microserviceApiError.code ?? microserviceApiError.message ?? 'Microservice API rejection'}`,
                    );
                } else {
                    toast.error(`Error ${(err as Error)?.name}: ${(err as Error)?.message ?? 'Something went wrong'}`);
                }
            }
            throw await withErrorHandler(async () => {
                return new MicroserviceProxyClientErr({
                    status: (err as MicroserviceProxyClientError)?.status ?? null,
                    code: (err as MicroserviceProxyClientError)?.code ?? (err as Error).name ?? 'Unknown error',
                    message:
                        (err as MicroserviceProxyClientError)?.message ?? (err as Error).name ?? 'Something went wrong',
                    details: (err as MicroserviceProxyClientError)?.details ?? err ?? null,
                });
            });
        }
    }

    async get<Res = any>(
        req: ProxyRequest,
        businessAccountId: string,
        skipErrorToast: boolean = false,
    ): Promise<Res | void> {
        return this.fetch({ ...req, method: 'GET' }, businessAccountId, skipErrorToast);
    }

    async post<Res = any>(
        req: ProxyRequest,
        businessAccountId: string,
        skipErrorToast: boolean = false,
    ): Promise<Res | void> {
        return this.fetch({ ...req, method: 'POST' }, businessAccountId, skipErrorToast);
    }

    async patch<Res = any>(req: ProxyRequest, businessAccountId: string): Promise<Res | void> {
        return this.fetch({ ...req, method: 'PATCH' }, businessAccountId);
    }

    async put<Res = any>(
        req: ProxyRequest,
        businessAccountId: string,
        skipErrorToast: boolean = false,
    ): Promise<Res | void> {
        return this.fetch({ ...req, method: 'PUT' }, businessAccountId, skipErrorToast);
    }

    async delete<Res = any>(req: ProxyRequest, businessAccountId: string): Promise<Res | void> {
        return this.fetch({ ...req, method: 'DELETE' }, businessAccountId);
    }

    protected async getHeaders(): Promise<{
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': string;
        authorization: string;
    }> {
        if (!this.getAccessTokenSilently) {
            throw new Error('proxyClient.getAccessTokenSilently is not set! Set this function before first use.');
        }
        const accessToken = await this.getAccessTokenSilently();
        return {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Content-Type': 'application/json',
            authorization: `${this.tokenPrefix} ${accessToken}`,
        };
    }

    static isProxySuccessResponse(obj: any): obj is ProxyResponse {
        return (
            obj &&
            typeof obj === 'object' &&
            typeof obj.status === 'number' &&
            [200, 201, 204].includes(obj.status) &&
            'response' in obj &&
            typeof obj.response === 'object' &&
            'headers' in obj &&
            typeof obj.headers === 'object'
        );
    }

    static isMicroserviceProxyClientError(obj: any): obj is MicroserviceProxyClientError {
        return (
            obj &&
            typeof obj === 'object' &&
            (typeof obj.status === 'number' || typeof obj.status === 'string') &&
            +obj.status >= 400 &&
            typeof obj.code === 'string' &&
            typeof obj.message === 'string'
        );
    }
}

export const proxyClient = new MicroserviceProxyClient();

export class MicroserviceProxyClientErr extends Error {
    code: string | number | null;

    status: string | number | null;

    details: any;

    message: string;

    constructor({
        code,
        status,
        message,
        details,
    }: {
        code: string | number | null;
        status: string | number | null;
        message: string;
        details: any;
    }) {
        super(message);
        this.code = code;
        this.message = message;
        this.status = status;
        this.details = details;
    }
}
